type ValueDict = Record<string, string | null>;

/**
 * One `claims` tool item, carried verbatim in `CorrectionResponse.slips`.
 * Counterpart: backend/src/brian/modules/agent/tools/claims.py::_item_view.
 */
export interface CorrectionSlip {
  status: "done" | "pending" | "failed" | "conflict";
  reason: string | null;
  current: ValueDict | null;
  understood: {
    order_uuid: string;
    label: string | null;
    target: string;
    claim: "quantity" | "product";
    from: ValueDict | null;
    to: ValueDict;
    said: string;
  } | null;
  changed: {
    kind:
      | "override"
      | "order_event"
      | "order"
      | "question"
      | "invoice_line"
      | "invoice";
    uuid: string;
  }[];
  pending: { kind: string; uuid: string; code?: string }[];
  needs_review: {
    kind: "question" | "invoice_line";
    uuid: string;
    prompt?: string;
    reason?: string;
    invoice_uuid?: string;
  }[];
  question_uuid: string | null;
  order: { label: string; quantity: number | string; unit: string } | null;
}

interface SlipDescription {
  understood: string;
  changed: string[];
  pending: string[];
  needsReview: string[];
}

const describeValue = (value: ValueDict | null | undefined): string => {
  if (!value) return "unknown";
  if (value.label) return value.label;
  if (value.quantity != null)
    return [value.quantity, value.unit].filter(Boolean).join(" ");
  return "unknown";
};

const CHANGED_LINES: Record<string, string> = {
  order_event: "Recorded the correction",
  order: "Recorded the correction",
  override: "Noted Brian's earlier belief",
  question: "Answered the open question",
  invoice_line: "Updated the draft invoice line",
  invoice: "Updated the draft invoice line",
};

const unique = (lines: string[]): string[] => [...new Set(lines)];

export const describeSlip = (slip: CorrectionSlip): SlipDescription => {
  const u = slip.understood;
  const understood = u
    ? `${u.label ?? u.target}: ${describeValue(u.from)} → ${describeValue(u.to)}`
    : "";

  if (slip.status === "failed")
    return {
      understood,
      changed: [],
      pending: [],
      needsReview: slip.reason ? [slip.reason] : [],
    };

  if (slip.status === "conflict")
    return {
      understood,
      changed: [
        `Brian's record changed since it looked (now ${describeValue(slip.current)}); nothing was written`,
      ],
      pending: [],
      needsReview: [],
    };

  return {
    understood,
    changed: unique(
      slip.changed.map((c) => CHANGED_LINES[c.kind]).filter(Boolean),
    ),
    pending: slip.pending.map((p) =>
      p.code ? `Invoice not updated (${p.code})` : "Invoice not updated",
    ),
    needsReview: slip.needs_review
      .map((n) => n.prompt ?? n.reason)
      .filter((line): line is string => !!line),
  };
};
